import React, { useEffect, useState, useCallback } from "react";
import { ShieldCheck, Ban, Sparkles, Send, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";

const STAGE_C = { new: "text-[#00E5FF]", contacted: "text-[#FFD700]", interested: "text-[#FFD700]", proposal: "text-[#FFB300]", negotiation: "text-[#FFB300]", won: "text-[#16A34A]", active: "text-[#16A34A]", renewal: "text-[#00E5FF]", lost: "text-[#FF0055]" };

export default function AdminLeads() {
  const [leads, setLeads] = useState(null);
  const [stages, setStages] = useState([]);
  const [forbidden, setForbidden] = useState(false);
  const [open, setOpen] = useState(null);
  const [draft, setDraft] = useState("");
  const [subject, setSubject] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/leads"); setLeads(data.leads); setStages(data.stages); }
    catch (e) { if ([401, 403].includes(e?.response?.status)) setForbidden(true); else toast.error(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStage = async (id, stage) => {
    try { await api.post("/admin/leads/update", { lead_id: id, stage }); toast.success("Updated"); await load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const genDraft = async (lead) => {
    setBusy(true); setDraft(""); setSubject("");
    try {
      const { data } = await api.post(`/admin/leads/${lead.id}/draft`);
      const txt = data.draft || "";
      const m = txt.match(/subject:\s*(.+)/i);
      setSubject(m ? m[1].trim() : `Advertise with Lagos Abuja Hot Life`);
      setDraft(txt.replace(/subject:\s*.+\n?/i, "").trim());
      setOpen(lead);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const send = async () => {
    setBusy(true);
    try { await api.post("/admin/leads/send", { lead_id: open.id, subject, body: draft }); toast.success("Outreach sent ✓"); setOpen(null); await load(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (forbidden) return <div className="min-h-[100dvh] bg-[#F8FAFC] flex flex-col items-center justify-center text-center px-6" data-testid="admin-forbidden"><Ban className="text-[#FF0055]" size={40} /><h1 className="font-display text-2xl mt-3">Admin only</h1></div>;

  return (
    <div className="min-h-[100dvh] bg-[#F8FAFC] px-5 py-6 max-w-2xl mx-auto" data-testid="admin-leads-page">
      <div className="flex items-center gap-2"><ShieldCheck className="text-[#FFD700]" size={24} /><h1 className="font-display text-2xl">Advertiser CRM</h1></div>
      <p className="text-sm text-slate-500 mt-1">Leads from the "Promote Your Business" form (opted-in) + imported prospects. AI drafts the pitch; we only send to opted-in contacts.</p>

      {leads === null ? <div className="flex justify-center py-10 text-slate-500"><Loader2 className="animate-spin" /></div>
        : leads.length === 0 ? <p className="mt-10 text-center text-slate-400">No leads yet.</p>
        : <div className="mt-5 space-y-2">
            {leads.map((l) => (
              <div key={l.id} data-testid={`lead-${l.id}`} className="bg-white border border-slate-200 rounded-xl p-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="font-semibold truncate">{l.business_name} {!l.opted_in && <span className="label-xs text-slate-400">(imported)</span>}</div>
                    <div className="label-xs text-slate-500 truncate">{l.contact_name} · {l.email} · {l.industry || "—"}</div>
                  </div>
                  <select data-testid={`lead-stage-${l.id}`} value={l.stage} onChange={(e) => setStage(l.id, e.target.value)} className={`bg-black/40 border border-slate-200 rounded-lg px-2 py-1 label-xs ${STAGE_C[l.stage] || ""}`}>
                    {stages.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <button data-testid={`lead-draft-${l.id}`} disabled={busy} onClick={() => genDraft(l)} className="mt-2 w-full label-xs bg-[#FFD700]/15 text-[#FFD700] py-1.5 rounded-lg font-bold flex items-center justify-center gap-1"><Sparkles size={12} /> AI draft outreach</button>
              </div>
            ))}
          </div>}

      {open && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-4" onClick={() => setOpen(null)}>
          <div className="bg-white border border-slate-200 rounded-2xl p-4 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="draft-modal">
            <div className="flex items-center gap-2 mb-2"><Mail size={16} className="text-[#FFD700]" /><h2 className="font-display text-lg">Outreach to {open.business_name}</h2></div>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full bg-black/40 border border-slate-200 rounded-lg px-3 py-2 text-sm mb-2" data-testid="draft-subject" />
            <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={9} className="w-full bg-black/40 border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="draft-body" />
            {open.opted_in ? (
              <button data-testid="draft-send" disabled={busy} onClick={send} className="mt-3 w-full bg-[#16A34A] text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-1"><Send size={14} /> Send email</button>
            ) : (
              <div className="mt-3 label-xs text-[#FFB300] bg-[#FFB300]/10 border border-[#FFB300]/30 rounded-lg p-2.5">This prospect didn't opt in. Copy this draft and send it from your own authorized email — cold sending through the platform isn't allowed.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
